import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Sheet,
  CircularProgress,
} from '@mui/joy';
import { authFetch } from '../utils/authFetch';
import {
  Users,
  Ambulance,
  LayoutPanelTop,
  ClipboardCheck,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts';
import { useNavigate } from 'react-router-dom';

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [weekdayStats, setWeekdayStats] = useState([]);
  const [loading, setLoading] = useState(true);

  const selectedOrg = localStorage.getItem('selectedOrg');

  useEffect(() => {
    if (!selectedOrg) return;

    async function fetchData() {
      try {
        const [totalRes, weekdayRes] = await Promise.all([
          authFetch(`${process.env.REACT_APP_SERVER_HOST}/org/${selectedOrg}/dashboard/total`),
          authFetch(`${process.env.REACT_APP_SERVER_HOST}/org/${selectedOrg}/dashboard/stats/weekday`),
        ]);

        if (!totalRes.ok) throw new Error('Errore fetch dashboard');
        if (!weekdayRes.ok) throw new Error('Errore fetch statistiche');

        const totalJson = await totalRes.json();
        const weekdayJson = await weekdayRes.json();

        setData(totalJson);

        setWeekdayStats(
          Array.isArray(weekdayJson)
            ? weekdayJson.map((item) => ({
                ...item,
                media_check: Number(item.media_check) || 0,
              }))
            : []
        );
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [selectedOrg]);

  if (!selectedOrg) {
    return (
      <Box>
        <Typography level="h3">Dashboard</Typography>
        <Typography>Seleziona un ente</Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box display="flex" alignItems="center" gap={2}>
        <CircularProgress size="sm" />
        <Typography>Caricamento...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography level="h3" mb={2}>
        Dashboard
      </Typography>

      <Grid container spacing={1}>
        <Widget
          title="Utenti"
          value={data.utenti_totale}
          inactive={Number(data.utenti_inattivi)}
          inactiveLabel="inattivi"
          icon={Users}
          path="/utenti"
        />

        <Widget
          title="Risorse"
          value={data.risorse_totale}
          inactive={Number(data.risorse_inattive)}
          inactiveLabel="inattive"
          icon={Ambulance}
          path="/risorse"
        />

        <Widget
          title="Categorie"
          value={data.categorie_totale}
          icon={LayoutPanelTop}
          path="/categorie"
        />

        <Widget
          title="Checklist"
          value={data.checklist_totale}
          icon={ClipboardCheck}
          path="/checklist"
        />
      </Grid>

      <Sheet
        variant="outlined"
        sx={{
          mt: 3,
          p: 2,
          borderRadius: 'xl',
          maxWidth: 720,
          background: 'linear-gradient(180deg, #fafafa 0%, #ffffff 100%)',
        }}
      >
        <Typography level="title-md" mb={0.5}>
          Media checklist settimanale
        </Typography>

        <Typography level="body-xs" color="neutral" mb={1.5}>
          Media giornaliera delle checklist
        </Typography>

        <Box sx={{ width: '100%', height: 190 }}>
          <ResponsiveContainer>
            <BarChart data={weekdayStats}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} opacity={0.2} />
              <XAxis dataKey="giorno" tick={{ fontSize: 11 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar
                dataKey="media_check"
                name="Media"
                radius={[8, 8, 0, 0]}
                fill="#3b82f6"
              />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </Sheet>
    </Box>
  );
}

function Widget({ title, value, inactive, inactiveLabel, icon: Icon, path }) {
  const navigate = useNavigate();

  return (
    <Grid xs="auto">
      <Sheet
        variant="outlined"
        onClick={() => path && navigate(path)}
        sx={{
          width: 200,
          height: 100,
          borderRadius: 'md',
          p: 1.5,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          position: 'relative',
          cursor: path ? 'pointer' : 'default',
          transition: '0.2s',
          '&:hover': path
            ? {
                boxShadow: 'md',
                transform: 'translateY(-2px)',
              }
            : {},
        }}
      >
        {typeof inactive === 'number' && (
          <Box
            sx={{
              position: 'absolute',
              top: 6,
              right: 8,
              px: 0.8,
              py: 0.2,
              borderRadius: 'xl',
              bgcolor: inactive > 0 ? 'danger.softBg' : 'success.softBg',
              color: inactive > 0 ? 'danger.700' : 'success.700',
              fontSize: 11,
              fontWeight: 600,
            }}
          >
            {inactive} {inactiveLabel}
          </Box>
        )}

        <Box display="flex" alignItems="center" gap={1}>
          {Icon && <Icon size={18} opacity={0.7} />}
          <Typography level="body-sm" fontWeight="lg">
            {title}
          </Typography>
        </Box>

        <Typography level="h2" lineHeight={1}>
          {value}
        </Typography>
      </Sheet>
    </Grid>
  );
}